import React, { useEffect, useState } from 'react';
import { Button, CardBody, Col, Form, Label, Modal, Row } from "reactstrap";
import '../../global/GlobalCSS.css';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { loadPendingCIIFileUpload } from 'VesselMaster/vesselMaster.hooks';
import { RootState } from 'index';
import { useDispatch, useSelector } from 'react-redux';
import Loading from 'Components/Loading';
import ErrorComponent from 'Components/ErrorComponent';
import { setVesselID } from 'Store/Generic/ReportingSlice';
import Dropzone from 'react-dropzone';
import { apiMedia } from 'global/api.global';
import { queryClient } from 'react-query/queryClient';
import { errResponse } from 'GenericForms/Helper';
import { errorToast } from 'Components/Toasts';

const CIIPendingFiles = (vesselId: any) => {
    /** State variables */
    const [modalState, setModalState] = useState(false);
    const [fileName, setFileName] = useState('');
    const [fileObject, setFileObject] = useState<any>({});
    const dispatch = useDispatch();
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    /** State variables end */

    /** useEffect start */
    useEffect(() => {
        dispatch(setVesselID(vesselId.vessel));
    }, [vesselId, dispatch])
    /** useEffect end */

    /** Queries */
    /** CII adjustment event's files that are not yet uploaded  */
    const { data: PendingFiles, isLoading: PendingFilesLoading, isError: PendingFilesError }:
        { data: any[], isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.PendingCIIFileUpload.key, VesselID],
            async () => {
                return await loadPendingCIIFileUpload(vesselId.vessel > 0 ? vesselId.vessel : VesselID);
            },
            { staleTime: Infinity }
        );
    /** Queries end */

    /** Open close modal */
    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    function tog_backdrop() {
        setModalState(!modalState);
        removeBodyCss();
    }

    const handleUploadButtonClick = (file: any, obj: any) => {
        setModalState(!modalState);
        setFileName(file);
        setFileObject(obj);
    }

    const handleAcceptedFiles = (files: any) => {
        fileObject.vessel_reporting_cii_adjustment_supporting_file_name = (files[0]);
        fileObject.file_status = true;
    }

    const PendingFileUploadSubmit = async () => {
        await apiMedia.put(`/vessel_reporting_cii_adjustment_supporting_file/${fileObject.id}/`, fileObject)
            .then(res => {
                if (res.status === 200) {
                    queryClient.invalidateQueries(queryKeyes.vessel.PendingCIIFileUpload.key);
                }
            }).catch(err => {
                if (errResponse.includes(err.response.status)) {
                    errorToast("Internal error occured, please contact the admin");
                }
            })
    }

    return (
        <>
            <Row>
                <Col lg={4}>
                    {PendingFilesLoading && <Loading message='Loading required data!' />}
                    {PendingFilesError && <ErrorComponent message='Unable to load required data!' />}
                </Col>
            </Row>
            {!PendingFilesLoading && !PendingFilesError &&
                <>
                    <CardBody className='p-0 pb-0 mt-2'>
                        <div className="table-responsive">
                            <table className="table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className='p-2 align-middle sr-no-width'>#</th>
                                        <th className='p-2 align-middle' style={{ width: '22%' }}>Duration</th>
                                        <th className='p-2 align-middle'>CII Adjustment event</th>
                                        <th className='p-2 align-middle'>Document</th>
                                        <th className='p-2 align-middle text-center'>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {PendingFiles.length === 0 ?
                                        <tr>
                                            <td colSpan={5} className='align-middle text-center'>
                                                No CII adjustment event files are pending for uploading
                                            </td>
                                        </tr>
                                        : PendingFiles.map((file: any, index: number) => {
                                            return (
                                                <tr key={index}>
                                                    <td className='align-middle p-2 text-center'>{index + 1}</td>
                                                    <td className='align-middle p-2'>
                                                        {`${file?.cii_begin_date_time.toString()?.substring(0, 10)} 
                                                            ${file?.cii_begin_date_time?.toString().substring(12, 16)} - 
                                                            ${file?.cii_end_date_time?.toString().substring(0, 10)}
                                                                ${file?.cii_end_date_time?.toString().substring(12, 16)}`}
                                                    </td>
                                                    <td className='align-middle p-2'>{file.cii_adjustment_name}</td>
                                                    <td className='align-middle p-2'>{file.cii_document_name}</td>
                                                    <td className='align-middle p-2 text-center'>
                                                        <Button
                                                            type="button"
                                                            className='btn-sm'
                                                            color="primary"
                                                            onClick={() => handleUploadButtonClick(file.cii_document_name, file)}>
                                                            Upload
                                                        </Button>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                    <Modal
                        size='md'
                        isOpen={modalState}
                        toggle={() => {
                            tog_backdrop();
                        }}
                        backdrop={"static"}
                        id="staticBackdrop"
                    >
                        <div className="modal-header">
                            <h5 className="modal-title" id="staticBackdropLabel">
                                Upload {fileName}
                            </h5>
                            <button
                                onClick={() => {
                                    setModalState(false);
                                }}
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label="Close"
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <Form className="needs-validation"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    PendingFileUploadSubmit();
                                    return false;
                                }}>
                                <Dropzone
                                    onDrop={(acceptedFiles) => {
                                        handleAcceptedFiles(acceptedFiles);
                                    }}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div className="dropzone custom-dropzone mt-3" >
                                            <div
                                                className="dz-message needsclick font_16"
                                                {...getRootProps()}
                                            >
                                                <input name={`file[]`} {...getInputProps()} />
                                                <div className="cloud-icon">
                                                    <i className="display-6 text-muted bx bx-cloud-upload" />
                                                </div>
                                                <Label className='mb-3'>Click here to upload.</Label>
                                            </div>
                                        </div>
                                    )}
                                </Dropzone>
                                <Row>
                                    <Col>
                                        <div className=" mt-3 mb-5">
                                            <Button type="submit" color='primary' className='pos-end'>Save</Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    </Modal>
                </>
            }
        </>
    )
}

export default CIIPendingFiles
